import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  MagnifyingGlass,
  DotsThreeVertical,
  Crown,
  Prohibit,
  Trash,
  Eye,
  ShieldCheck,
  ShieldSlash,
  UserPlus,
  ArrowLeft,
  ArrowRight,
  X,
} from "@phosphor-icons/react";
import { api, formatBRL, formatNum, formatDate, initials, paletteFor } from "@/lib/gado";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";

const PAGE_SIZE = 10;

function UserStatusBadges({ u }) {
  return (
    <span className="inline-flex items-center gap-1 flex-wrap">
      {u.role === "super_admin" && (
        <Badge variant="outline" className="border-gado-gold/40 bg-gado-gold/10 text-gado-gold text-[10px]">Admin</Badge>
      )}
      <Badge
        variant="outline"
        className={u.plan === "pro"
          ? "border-gado-green/30 bg-gado-green/10 text-gado-green text-[10px]"
          : "border-gado-line text-gado-muted text-[10px]"}
      >
        {u.plan === "pro" ? "Pro" : "Grátis"}
      </Badge>
      {u.blocked && (
        <Badge variant="outline" className="border-destructive/40 bg-destructive/10 text-destructive text-[10px]">Bloqueado</Badge>
      )}
    </span>
  );
}

export default function AdminUsers() {
  const { user: me } = useAuth();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [qApplied, setQApplied] = useState("");
  const [plan, setPlan] = useState("todos");
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [promoteEmail, setPromoteEmail] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-users", qApplied, plan, page],
    queryFn: async () =>
      (await api.get("/admin/users", {
        params: { q: qApplied || undefined, plan: plan === "todos" ? undefined : plan, page, page_size: PAGE_SIZE },
      })).data,
  });

  const { data: admins = [] } = useQuery({
    queryKey: ["admin-admins"],
    queryFn: async () => (await api.get("/admin/admins")).data,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin-users"] });
    qc.invalidateQueries({ queryKey: ["admin-admins"] });
  };

  const doAction = async (fn, successMsg) => {
    try {
      await fn();
      invalidate();
      toast.success(successMsg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha na ação");
    }
  };

  const togglePlan = (u) =>
    doAction(
      () => api.put(`/admin/users/${u.user_id}/plan`, { plan: u.plan === "pro" ? "free" : "pro" }),
      `Plano de ${u.email} alterado para ${u.plan === "pro" ? "grátis" : "Pro"}`
    );

  const toggleBlock = (u) =>
    doAction(
      () => api.post(`/admin/users/${u.user_id}/block`, { blocked: !u.blocked }),
      u.blocked ? "Usuário desbloqueado" : "Usuário bloqueado"
    );

  const toggleRole = (u) =>
    doAction(
      () => api.put(`/admin/users/${u.user_id}/role`, { role: u.role === "super_admin" ? "cliente" : "super_admin" }),
      u.role === "super_admin" ? "Acesso admin removido" : `${u.email} agora é administrador`
    );

  const promoteByEmail = async () => {
    const email = promoteEmail.trim().toLowerCase();
    if (!email) return;
    try {
      const { data: found } = await api.get("/admin/users", { params: { q: email, page_size: 5 } });
      const target = found.items.find((x) => x.email === email);
      if (!target) {
        toast.error("Este e-mail não possui conta no app — peça para se cadastrar primeiro");
        return;
      }
      await api.put(`/admin/users/${target.user_id}/role`, { role: "super_admin" });
      invalidate();
      setPromoteEmail("");
      toast.success(`${email} promovido a administrador`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao promover");
    }
  };

  const openDetail = async (u) => {
    try {
      const { data } = await api.get(`/admin/users/${u.user_id}`);
      setDetail(data);
    } catch {
      toast.error("Falha ao carregar detalhes");
    }
  };

  const doDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/admin/users/${toDelete.user_id}`);
      invalidate();
      toast.success("Conta e dados excluídos (LGPD)");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao excluir");
    } finally {
      setToDelete(null);
    }
  };

  const items = data?.items || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-5" data-testid="admin-users-page">
      <div>
        <h1 className="font-cabinet text-2xl md:text-3xl font-bold text-gado-ink tracking-tight">Usuários</h1>
        <p className="text-gado-muted text-sm">Gerencie contas, planos, acessos e administradores.</p>
      </div>

      {/* Administradores + promover por e-mail */}
      <div className="rounded-xl border border-gado-gold/30 bg-gado-gold/5 p-4" data-testid="admins-card">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck size={16} weight="fill" className="text-gado-gold" />
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-gado-ink">Administradores</p>
        </div>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {admins.map((a) => (
            <span
              key={a.user_id}
              className="inline-flex items-center gap-1.5 rounded-full bg-white border border-gado-gold/30 px-2.5 py-1 text-xs text-gado-ink"
              data-testid={`admin-chip-${a.email}`}
            >
              {a.email}
              {a.user_id !== me?.user_id && (
                <button
                  onClick={() => doAction(
                    () => api.put(`/admin/users/${a.user_id}/role`, { role: "cliente" }),
                    `Acesso admin de ${a.email} removido`
                  )}
                  className="text-gado-muted hover:text-destructive"
                  aria-label={`Remover admin ${a.email}`}
                  data-testid={`demote-${a.email}`}
                >
                  <X size={12} weight="bold" />
                </button>
              )}
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            value={promoteEmail}
            onChange={(e) => setPromoteEmail(e.target.value)}
            placeholder="E-mail de um usuário cadastrado"
            data-testid="promote-email-input"
            className="bg-white"
            onKeyDown={(e) => e.key === "Enter" && promoteByEmail()}
          />
          <Button onClick={promoteByEmail} data-testid="promote-email-button" className="bg-gado-green hover:bg-gado-greenHover text-white shrink-0">
            <UserPlus size={16} className="mr-1.5" /> Promover
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-2.5">
        <div className="relative flex-1">
          <MagnifyingGlass size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-gado-muted" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { setQApplied(q); setPage(1); } }}
            placeholder="Buscar por nome, e-mail ou fazenda… (Enter)"
            className="pl-9 bg-white"
            data-testid="admin-user-search"
          />
        </div>
        <Select value={plan} onValueChange={(v) => { setPlan(v); setPage(1); }}>
          <SelectTrigger className="w-full sm:w-44 bg-white" data-testid="admin-plan-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos os planos</SelectItem>
            <SelectItem value="pro">Pro</SelectItem>
            <SelectItem value="free">Grátis</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Lista */}
      <div className="space-y-2" data-testid="admin-users-list">
        {isLoading && <p className="text-sm text-gado-muted text-center py-8">Carregando…</p>}
        {!isLoading && items.length === 0 && (
          <p className="text-sm text-gado-muted text-center py-8">Nenhum usuário encontrado.</p>
        )}
        {items.map((u) => (
          <div
            key={u.user_id}
            className="rounded-xl border border-gado-line bg-white px-3.5 py-3 flex items-center gap-3 min-w-0"
            data-testid={`admin-user-${u.user_id}`}
          >
            <span className={`h-10 w-10 shrink-0 rounded-xl grid place-items-center font-cabinet font-bold text-sm ${paletteFor(u.name || u.email)}`}>
              {initials(u.name || u.email)}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 min-w-0">
                <p className="text-sm font-bold text-gado-ink truncate">{u.name || "—"}</p>
                <UserStatusBadges u={u} />
              </div>
              <p className="text-xs text-gado-muted truncate">
                {u.email} · {formatNum(u.transactions_count, 0)} pesagens
                {u.last_login_at ? ` · entrou ${formatDate(u.last_login_at)}` : ""}
              </p>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="p-1.5 rounded-md text-gado-muted hover:bg-gado-line/60 shrink-0"
                  data-testid={`admin-user-actions-${u.user_id}`}
                  aria-label="Ações do usuário"
                >
                  <DotsThreeVertical size={20} weight="bold" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => openDetail(u)} data-testid={`view-${u.user_id}`}>
                  <Eye size={16} className="mr-2" /> Ver detalhes
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => toggleRole(u)} data-testid={`role-${u.user_id}`}>
                  {u.role === "super_admin" ? (
                    <><ShieldSlash size={16} className="mr-2" /> Remover admin</>
                  ) : (
                    <><ShieldCheck size={16} className="mr-2" /> Tornar admin</>
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => togglePlan(u)} data-testid={`plan-${u.user_id}`}>
                  <Crown size={16} className="mr-2" /> {u.plan === "pro" ? "Mudar para Grátis" : "Mudar para Pro"}
                </DropdownMenuItem>
                {u.role !== "super_admin" && (
                  <DropdownMenuItem onClick={() => toggleBlock(u)} data-testid={`block-${u.user_id}`}>
                    <Prohibit size={16} className="mr-2" /> {u.blocked ? "Desbloquear" : "Bloquear"}
                  </DropdownMenuItem>
                )}
                {u.role !== "super_admin" && (
                  <DropdownMenuItem onClick={() => setToDelete(u)} className="text-destructive focus:text-destructive" data-testid={`delete-${u.user_id}`}>
                    <Trash size={16} className="mr-2" /> Excluir (LGPD)
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ))}
      </div>

      {/* Paginação */}
      <div className="flex items-center justify-between" data-testid="admin-users-pagination">
        <p className="text-xs text-gado-muted">{total} usuário(s) · página {page} de {totalPages}</p>
        <div className="flex gap-1.5">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="page-prev">
            <ArrowLeft size={14} />
          </Button>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="page-next">
            <ArrowRight size={14} />
          </Button>
        </div>
      </div>

      {/* Detalhes do usuário */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-md" data-testid="admin-user-detail">
          <DialogHeader>
            <DialogTitle className="text-gado-ink">{detail?.user?.name || "Usuário"}</DialogTitle>
            <DialogDescription className="text-xs">{detail?.user?.email}</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2"><UserStatusBadges u={detail.user} /></div>
              <div className="rounded-lg bg-gado-bg/60 p-3 space-y-1.5">
                <p className="flex justify-between"><span className="text-gado-muted">Pesagens</span><b className="tabular-nums">{formatNum(detail.usage.pesagens, 0)}</b></p>
                <p className="flex justify-between"><span className="text-gado-muted">Animais</span><b className="tabular-nums">{formatNum(detail.usage.animais, 0)} cab</b></p>
                <p className="flex justify-between"><span className="text-gado-muted">Arrobas</span><b className="tabular-nums">{formatNum(detail.usage.arrobas, 1)} @</b></p>
                <p className="flex justify-between"><span className="text-gado-muted">Valor movimentado</span><b className="tabular-nums">{formatBRL(detail.usage.valor_total)}</b></p>
                <p className="flex justify-between"><span className="text-gado-muted">Análises de IA</span><b className="tabular-nums">{formatNum(detail.usage.analises_ia, 0)}</b></p>
                <p className="flex justify-between"><span className="text-gado-muted">Última pesagem</span><b>{detail.usage.ultima_pesagem ? formatDate(detail.usage.ultima_pesagem) : "—"}</b></p>
                <p className="flex justify-between"><span className="text-gado-muted">Conta criada</span><b>{formatDate(detail.user.created_at)}</b></p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Exclusão LGPD */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir conta definitivamente?</AlertDialogTitle>
            <AlertDialogDescription>
              A conta de <strong>{toDelete?.email}</strong> e TODOS os dados vinculados
              (pesagens, análises, cadastros) serão removidos. Ação irreversível (LGPD).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="admin-confirm-delete" className="bg-destructive hover:bg-destructive/90">
              Excluir definitivamente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
